import { useWorkplaceStore } from 'core/session/workplaceStore';
import { evaluateLoginGates } from 'module/auth/utils/loginGates';
import { ROUTES } from 'module/navigation/enums/routes.enums';
import { ReactNode } from 'react';
import { useQuery } from 'react-query';
import { Redirect } from 'react-router-dom';

export const LOGIN_GATES_QUERY_KEY = 'login-gates';

/**
 * Keeps the app shell closed until the post-login steps are done: a workplace
 * is selected, the current legal agreement is accepted and any forced password
 * change is complete. Otherwise the user is sent back to the login flow, which
 * shows the outstanding step (LoginPage opens ChooseWorkplace when
 * `location.state` is set).
 *
 * UI enforcement only (SEC-014 remains open until the API enforces it too).
 */
export function SessionGate({ children }: { children: ReactNode }): JSX.Element | null {
	const workplaceId = useWorkplaceStore((state) => state.workplaceId);

	// Re-evaluated on every shell mount (not cached), so finishing a step in the
	// login flow is seen immediately and cannot cause a redirect loop.
	const { data: gates, isLoading, isError } = useQuery(
		LOGIN_GATES_QUERY_KEY,
		evaluateLoginGates,
		{
			enabled: workplaceId !== null,
			cacheTime: 0,
			staleTime: 0,
			retry: false,
			refetchOnWindowFocus: false,
		}
	);

	const backToLoginFlow = <Redirect to={{ pathname: ROUTES.LOGIN, state: { gate: true } }} />;

	if (workplaceId === null) return backToLoginFlow;
	if (isLoading) return null;
	// Fail closed when the agreement check errors (REL-001).
	if (isError || !gates || gates.needsEula || gates.needsPassword) return backToLoginFlow;

	return <>{children}</>;
}
